import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "./config";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Row, Col, Button, Form, Modal } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
axios.defaults.maxContentLength = Infinity;
axios.defaults.maxBodyLength = Infinity;

const AUDIO_NAME_MAX_LENGTH = 100;
const ALLOWED_AUDIO_EXTENSIONS = [
  ".mp3",
  ".wav",
  ".ogg",
  ".oga",
  ".m4a",
  ".aac",
  ".flac",
  ".opus",
  ".webm",
  ".mp4",
  ".mpeg",
  ".mpga",
];

const getAudioExtension = (fileName = "") => {
  const lastDot = fileName.lastIndexOf(".");
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : "";
};

const isAudioFile = (file) => {
  const mimeType = String(file?.type || "").toLowerCase();
  const extension = getAudioExtension(file?.name || "");
  return mimeType.startsWith("audio/") || ALLOWED_AUDIO_EXTENSIONS.includes(extension);
};

const getAudioValidationMessage = (files = []) => {
  const filesToValidate = files.filter(
    (file) => typeof File !== "undefined" && file instanceof File
  );

  const invalidTypeFiles = filesToValidate.filter((file) => !isAudioFile(file));
  if (invalidTypeFiles.length > 0) {
    return `O arquivo "${invalidTypeFiles[0].name}" nao foi reconhecido como audio.`;
  }

  const longNameFile = filesToValidate.find(
    (file) => String(file?.name || "").trim().length > AUDIO_NAME_MAX_LENGTH
  );
  if (longNameFile) {
    return `O nome do arquivo "${longNameFile.name}" excede ${AUDIO_NAME_MAX_LENGTH} caracteres. Renomeie e tente novamente.`;
  }

  return null;
};

const getUploadErrorMessage = (error) => {
  const responseData = error?.response?.data;

  if (typeof responseData === "string" && responseData.trim()) {
    return responseData;
  }

  if (responseData?.msg || responseData?.message || responseData?.error) {
    return responseData.msg || responseData.message || responseData.error;
  }

  if (error?.response?.status === 401 || error?.response?.status === 403) {
    return "Sua sessao nao permite cadastrar audios. Entre novamente e tente de novo.";
  }

  if (error?.response?.status >= 500) {
    return "O servidor falhou ao processar os audios. Verifique o nome dos arquivos e tente novamente.";
  }

  if (error?.request) {
    const chunkNumber = error?.audioChunkMeta?.chunkNumber;
    const totalChunks = error?.audioChunkMeta?.totalChunks;
    if (chunkNumber && totalChunks) {
      return `Nao foi possivel enviar o lote ${chunkNumber} de ${totalChunks} dos audios. Tente novamente.`;
    }
    return "Nao foi possivel enviar os audios para o servidor. Verifique a conexao e tente novamente.";
  }

  return "Erro ao processar o audio.";
};

const AUDIO_UPLOAD_MAX_FILES_PER_CHUNK = 5;
const AUDIO_UPLOAD_MAX_BYTES_PER_CHUNK = 20 * 1024 * 1024;

const buildFileSummary = (files = []) => ({
  total: files.length,
  totalBytes: files.reduce((sum, file) => sum + Number(file?.size || 0), 0),
  sampleNames: files.slice(0, 5).map((file) => file?.name || "sem-nome"),
});

const logAudioFlow = (step, details = {}) => {
  console.log(`[CadastroAudio] ${step}`, details);
};

const buildAudioUploadChunks = (files = []) => {
  const chunks = [];
  let currentChunk = [];
  let currentBytes = 0;

  files.forEach((file) => {
    const fileSize = Number(file?.size || 0);
    const shouldStartNewChunk =
      currentChunk.length > 0 &&
      (
        currentChunk.length >= AUDIO_UPLOAD_MAX_FILES_PER_CHUNK ||
        currentBytes + fileSize > AUDIO_UPLOAD_MAX_BYTES_PER_CHUNK
      );

    if (shouldStartNewChunk) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentBytes = 0;
    }

    currentChunk.push(file);
    currentBytes += fileSize;
  });

  if (currentChunk.length > 0) {
    chunks.push(currentChunk);
  }

  return chunks;
};

const uploadAudioChunks = async ({ method, url, files, headers, contextLabel }) => {
  const chunks = buildAudioUploadChunks(files);
  const totalChunks = chunks.length;

  for (let index = 0; index < chunks.length; index += 1) {
    const chunkNumber = index + 1;
    const chunk = chunks[index];
    const audioFormData = new FormData();

    chunk.forEach((audio) => {
      audioFormData.append("audios", audio);
    });

    logAudioFlow(`${contextLabel} - lote ${chunkNumber}/${totalChunks} iniciando`, {
      url,
      method,
      chunkNumber,
      totalChunks,
      summary: buildFileSummary(chunk),
    });

    let response;
    try {
      response = await axios({
        method,
        url,
        data: audioFormData,
        headers,
      });
    } catch (error) {
      error.audioChunkMeta = {
        chunkNumber,
        totalChunks,
        summary: buildFileSummary(chunk),
        url,
        method,
      };
      throw error;
    }

    logAudioFlow(`${contextLabel} - lote ${chunkNumber}/${totalChunks} concluido`, {
      url,
      status: response.status,
      response: response.data,
    });
  }
};

const CadastroAudio = ({ audioID }) => {
  const AUDIO_TOAST_CONTAINER_ID = "cadastro-audio-toast";
  const navigate = useNavigate();
  const location = useLocation();
  const goBack = () => {
    const from = location.state?.from;
    if (typeof from === "string" && from.startsWith("/")) {
      navigate(from, { replace: true });
      return;
    }
    navigate("/audios");
  };
  const estadoInicial = {
    cp_curso_id: "",
    cp_audio: [],
    cp_youtube_link_curso: "",
    cp_pdfs: [],
  };
  const [isLoading, setIsLoading] = useState(false);
  const [cursos, setCursos] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [audioData, setAudioData] = useState(estadoInicial);
  const [removedAllAudios, setRemovedAllAudios] = useState(false);

  const notifySuccess = (message, options = {}) =>
    toast.success(message, {
      containerId: AUDIO_TOAST_CONTAINER_ID,
      ...options,
    });

  const notifyError = (message, options = {}) =>
    toast.error(message, {
      containerId: AUDIO_TOAST_CONTAINER_ID,
      ...options,
    });

  // Lista fixa de cursos (opções)
  const opcoesCursos = [
    { value: "FERRIS WHEEL 1", label: "[ING] - FERRIS WHEEL 1" },
    { value: "FERRIS WHEEL 2", label: "[ING] - FERRIS WHEEL 2" },
    { value: "FERRIS WHEEL 3", label: "[ING] - FERRIS WHEEL 3" },
    { value: "BEST BUDDIES 1", label: "[ING] - BEST BUDDIES 1" },
    { value: "BEST BUDDIES 2", label: "[ING] - BEST BUDDIES 2" },
    { value: "BEST BUDDIES 3", label: "[ING] - BEST BUDDIES 3" },
    { value: "KIDS 1", label: "[ING] - Kids 1" },
    { value: "KIDS 2", label: "[ING] - Kids 2" },
    { value: "NEXT STATION STARTER", label: "[ING] - NEXT STATION STARTER" },
    { value: "NEXT STATION 2", label: "[ING] - NEXT STATION 2" },
    { value: "NEXT STATION 3", label: "[ING] - NEXT STATION 3" },
    { value: "CIPEX TWEENS 1", label: "[ING] - CIPEX TWEENS 1" },
    { value: "CIPEX TWEENS 2", label: "[ING] - CIPEX TWEENS 2" },
    { value: "CIPEX TWEENS 3", label: "[ING] - CIPEX TWEENS 3" },
    { value: "CIPEX TWEENS 4", label: "[ING] - CIPEX TWEENS 4" },
    { value: "CIPEX TWEENS 5", label: "[ING] - CIPEX TWEENS 5" },
    { value: "CIPEX TWEENS 6", label: "[ING] - CIPEX TWEENS 6" },
    { value: "CIPEX TWEENS 7", label: "[ING] - CIPEX TWEENS 7" },
    { value: "CIPEX ENGLISH BOOK 1", label: "[ING] - CIPEX ENGLISH BOOK 1" },
    { value: "CIPEX ENGLISH BOOK 2", label: "[ING] - CIPEX ENGLISH BOOK 2" },
    { value: "CIPEX ENGLISH BOOK 3", label: "[ING] - CIPEX ENGLISH BOOK 3" },
    { value: "CIPEX ENGLISH BOOK 4", label: "[ING] - CIPEX ENGLISH BOOK 4" },
    { value: "CIPEX ENGLISH BOOK 5", label: "[ING] - CIPEX ENGLISH BOOK 5" },
    { value: "CIPEX ENGLISH BOOK 6", label: "[ING] - CIPEX ENGLISH BOOK 6" },
    { value: "CIPEX ENGLISH BUSINESS SERIES", label: "[ING] - CIPEX ENGLISH BUSINESS SERIES" },
    { value: "CIPEX ENGLISH BOOK 6 NEW EDITION", label: "[ING] - CIPEX ENGLISH BOOK 6 NEW EDITION" },
    { value: "CIPEX ENGLISH BOOK 7", label: "[ING] - CIPEX ENGLISH BOOK 7" },
    { value: "CIPEX ENGLISH TEENS 8", label: "[ING] - CIPEX ENGLISH TEENS 8" },
    { value: "CIPEX ENGLISH TEENS 9", label: "[ING] - CIPEX ENGLISH TEENS 9" },
    { value: "TV BOX CONVERSATION VOL. 1", label: "[ING] - TV BOX CONVERSATION VOL. 1" },
    { value: "TV BOX CONVERSATION VOL. 2", label: "[ING] - TV BOX CONVERSATION VOL. 2" },
    { value: "THE BUSINESS PRE-INTERMEDIATE", label: "[ING] - The Business Pre-Intermediate" },
    { value: "THE BUSINESS INTERMEDIATE", label: "[ING] - The Business Intermediate" },
    { value: "THE BUSINESS UPPER-INTERMEDIATE", label: "[ING] - The Business Upper-Intermediate" },
    { value: "THE BUSINESS ADVANCED", label: "[ING] - The Business Advanced" },
    { value: "IN COMPANY 3.0 PRE-INTERMEDIATE", label: "[ING] - In Company 3.0 Pre-Intermediate" },
    { value: "IN COMPANY 3.0 INTERMEDIATE", label: "[ING] - In Company 3.0 Intermediate" },
    { value: "IN COMPANY 3.0 UPPER-INTERMEDIATE", label: "[ING] - In Company 3.0 Upper-Intermediate" },
    { value: "IN COMPANY 3.0 ADVANCED", label: "[ING] - In Company 3.0 Advanced" },
    { value: "NUEVO ESPAÑOL EN MARCHA 1", label: "[ESP] - NUEVO ESPAÑOL EN MARCHA 1" },
    { value: "NUEVO ESPAÑOL EN MARCHA 2", label: "[ESP] - NUEVO ESPAÑOL EN MARCHA 2" },
    { value: "NUEVO ESPAÑOL EN MARCHA 3", label: "[ESP] - NUEVO ESPAÑOL EN MARCHA 3" },
    { value: "NUEVO ESPAÑOL EN MARCHA 4", label: "[ESP] - NUEVO ESPAÑOL EN MARCHA 4" },
    { value: "ALFABETIZACAO", label: "[ALE] - ALFABETIZAÇÃO" },
    { value: "MOMENTE A1", label: "[ALE] - MOMENTE A1" },
    { value: "MOMENTE A2", label: "[ALE] - MOMENTE A2" },
    { value: "MOMENTE B1", label: "[ALE] - MOMENTE B1" },
    { value: "ASPEKTE B1", label: "[ALE] - ASPEKTE B1" },
    { value: "ASPEKTE B2", label: "[ALE] - ASPEKTE B2" },
    { value: "DAF+", label: "[ALE] - DAF+" },
    { value: "RUSSO A1", label: "[RUS] RUSSO A1" },
    
    // { value: "TESTE", label: "[TT] - TESTE" }
  ];

  useEffect(() => {
    fetchCursos();
  }, []);

  // useEffect(() => {
  //   if (audioID) {
  //     axios.get(`${API_BASE_URL}/audios/${audioID}`)
  //       .then((response) => {
  //         setAudioData(response.data);
  //       })
  //       .catch((error) => {
  //         toast.error("Erro ao carregar dados do áudio");
  //       });
  //   }
  // }, [audioID]);

  useEffect(() => {
    if (audioID) {
      buscarCurso(audioID);
      buscarAudiosDoCurso(audioID);
    }
  }, [audioID]);

  const buscarCurso = async (idCurso) => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/cursos/${idCurso}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const link = resp.data.cp_youtube_link_curso
      const courseName = resp.data.cp_nome_curso;
      const matchingOption = opcoesCursos.find(option => option.value === courseName);
      setAudioData((dadosAntigos) => ({
        ...dadosAntigos,
        cp_curso_id: matchingOption ? matchingOption.value : "",
        // cp_youtube_link_curso: resp.data.cp_youtube_link_curso || "",
        cp_youtube_link_curso: link == "null" ? "" : link,
        cp_pdfs: [
          resp.data.cp_pdf1_curso,
          resp.data.cp_pdf2_curso,
          resp.data.cp_pdf3_curso,
        ].filter(Boolean), // remove nulls
        nomeCurso: courseName,
      }));

    } catch (erro) {
      notifyError("Erro ao carregar curso");
    }
  };



  const buscarAudiosDoCurso = async (idCurso) => {
    try {
      const resp = await axios.get(`${API_BASE_URL}/audios-curso/${idCurso}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAudioData((dadosAntigos) => ({
        ...dadosAntigos,
        cp_audio: resp.data // array de áudios do curso
      }));
      // console.log("Nomes dos áudios:", resp.data.map(audio => audio.cp_nome_audio));
    } catch (erro) {
      notifyError("Erro ao carregar áudios");
    }
  };



  const fetchCursos = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/cursos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setCursos(response.data);
    } catch (error) {
      console.error("[CadastroAudio] Falha no fluxo", {
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status || null,
        response: error.response?.data || null,
      });
      console.error("Erro ao buscar os cursos:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAudioData((prevAudioData) => ({ ...prevAudioData, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { files, name } = e.target;
    if (name === "cp_audio") {
      const arquivosSelecionados = Array.from(files || []);
      const audioValidationMessage = getAudioValidationMessage(arquivosSelecionados);
      if (audioValidationMessage) {
        notifyError(audioValidationMessage, { autoClose: 7000 });
        e.target.value = "";
        return;
      }

      const novosAudios = [...(audioData.cp_audio || []), ...arquivosSelecionados];
      setAudioData((prevAudioData) => ({ ...prevAudioData, cp_audio: novosAudios }));
    } else if (name === "cp_pdfs") {
      if (files.length + audioData.cp_pdfs.length > 3) {
        notifyError("Máximo de 3 PDFs permitidos.");
        return;
      }
      const novosPdfs = [...(audioData.cp_pdfs || []), ...Array.from(files)].slice(0, 3);
      setAudioData((prevAudioData) => ({ ...prevAudioData, cp_pdfs: novosPdfs }));
    }
  };

  const removeFile = (index, type) => {
    if (type === "audio") {
      setRemovedAllAudios(true);
      setAudioData((prevAudioData) => ({ ...prevAudioData, cp_audio: [] }));
    } else if (type === "pdf") {
      setAudioData((prevAudioData) => ({
        ...prevAudioData,
        cp_pdfs: prevAudioData.cp_pdfs.filter((_, i) => i !== index),
      }));
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    const audioValidationMessage = getAudioValidationMessage(audioData.cp_audio);
    if (audioValidationMessage) {
      notifyError(audioValidationMessage, { autoClose: 7000 });
      return;
    }

    setIsLoading(true);
    let createdCursoId = null;
    const authHeaders = {
      Authorization: `Bearer ${localStorage.getItem('token')}`
    };
    const totalAudiosSelecionados = audioData.cp_audio.filter((audio) => isAudioFile(audio));

    console.groupCollapsed(`[CadastroAudio] Inicio ${audioID ? "edicao" : "cadastro"}`);
    logAudioFlow("01 - Dados iniciais", {
      modo: audioID ? "edicao" : "cadastro",
      audioID: audioID || null,
      curso: audioData.cp_curso_id,
      youtube: audioData.cp_youtube_link_curso,
      pdfs: buildFileSummary(audioData.cp_pdfs),
      audios: buildFileSummary(totalAudiosSelecionados),
      removedAllAudios,
    });
    let success = false;
    try {
      if (audioID) {
        // 1. Atualiza dados do curso (nome, link e PDFs)
        const cursoFormData = new FormData();
        cursoFormData.append("cp_nome_curso", audioData.cp_curso_id);
        cursoFormData.append("cp_youtube_link_curso", audioData.cp_youtube_link_curso || "");

        audioData.cp_pdfs.forEach((pdf, index) => {
          if (pdf instanceof File) {
            cursoFormData.append(`pdf${index + 1}`, pdf);
          }
        });

        logAudioFlow("02 - Atualizando curso/PDFs", {
          url: `${API_BASE_URL}/update-curso/${audioID}`,
          pdfs: buildFileSummary(audioData.cp_pdfs),
        });

        const updateCursoResponse = await axios.put(`${API_BASE_URL}/update-curso/${audioID}`, cursoFormData, {
          headers: authHeaders,
        });

        logAudioFlow("03 - Curso/PDFs atualizados", {
          status: updateCursoResponse.status,
          response: updateCursoResponse.data,
        });

        if (removedAllAudios) {
          logAudioFlow("04 - Removendo audios antigos", {
            url: `${API_BASE_URL}/audios-curso/${audioID}`,
          });

          const deleteResponse = await axios.delete(`${API_BASE_URL}/audios-curso/${audioID}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });

          logAudioFlow("05 - Audios antigos removidos", {
            status: deleteResponse.status,
            response: deleteResponse.data,
          });
          setRemovedAllAudios(false);
        }

        // 3. Upload Incremental (Chunked)
        const audiosNovos = totalAudiosSelecionados;

        if (audiosNovos.length > 0) {
          await uploadAudioChunks({
            method: "put",
            url: `${API_BASE_URL}/append-audios/${audioID}`,
            files: audiosNovos,
            headers: authHeaders,
            contextLabel: "06 - Upload de audios da edicao",
          });
        }

        notifySuccess("Curso e áudios atualizados com sucesso!");
        success = true;
      } else {
        const cursoFormData = new FormData();
        cursoFormData.append("cp_nome_curso", audioData.cp_curso_id);
        cursoFormData.append("cp_youtube_link_curso", audioData.cp_youtube_link_curso);

        audioData.cp_pdfs.forEach((pdf, index) => {
          cursoFormData.append(`pdf${index + 1}`, pdf);
        });

        logAudioFlow("02 - Criando curso/PDFs", {
          url: `${API_BASE_URL}/cursos`,
          pdfs: buildFileSummary(audioData.cp_pdfs),
        });

        const response = await axios.post(`${API_BASE_URL}/cursos`, cursoFormData, {
          headers: authHeaders,
        });
        const cursoId = response.data.cursoId;
        createdCursoId = cursoId;

        logAudioFlow("03 - Curso criado", {
          status: response.status,
          cursoId,
          response: response.data,
        });

        if (totalAudiosSelecionados.length > 0) {
          await uploadAudioChunks({
            method: "post",
            url: `${API_BASE_URL}/register-audio/${cursoId}`,
            files: totalAudiosSelecionados,
            headers: authHeaders,
            contextLabel: "04 - Upload de audios do cadastro",
          });
        }

        notifySuccess("Curso e áudios cadastrados com sucesso!");
        setAudioData(estadoInicial);
        success = true;
      }
    } catch (error) {
      console.error("❌ Erro ao processar o áudio:", error);
      
      // Log detalhado para depuração
      if (error.response) {
        console.log("Status do erro:", error.response.status);
        console.log("Dados do erro:", error.response.data);
      }

      console.error("[CadastroAudio] Falha no fluxo", {
        message: error.message,
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status || null,
        response: error.response?.data || null,
        chunk: error.audioChunkMeta || null,
      });

      const errorMessage = getUploadErrorMessage(error);
      notifyError(errorMessage, { autoClose: 8000 });

      const rollbackCursoId = createdCursoId || error.response?.data?.cursoId;
      if (!audioID && rollbackCursoId) {
        try {
          logAudioFlow("Rollback - Removendo curso criado parcialmente", {
            cursoId: rollbackCursoId,
          });
          await axios.delete(`${API_BASE_URL}/cursos/${rollbackCursoId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
        } catch (rollbackError) {
          console.error("Erro ao remover curso criado parcialmente:", rollbackError);
        }
      }
    } finally {
      console.groupEnd();
      setIsLoading(false);
      if (success) {
        goBack();
      }
    }
  };


  const handleConfirmSubmit = (e) => {
    e.preventDefault();
    setShowConfirmModal(false);
    handleSubmit(e);
  };

  return (
    <div>
      {isLoading && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div className="spinner-border text-light" role="status"></div>
        </div>
      )}

      <form className="form-container-cad" onSubmit={(e) => e.preventDefault()}>
        <Row>
          <Col md={6}>
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title mb-0">Informações do Áudio</h6>
              </div>
              <div className="card-body">
                <Form.Control
                  as="select"
                  id="cp_curso_id"
                  name="cp_curso_id"
                  value={audioData.cp_curso_id} // Esse value determina a seleção
                  onChange={handleChange}

                  required
                >
                  <option value="">Selecione o curso</option>
                  {opcoesCursos.map((curso) => (
                    <option key={curso.value} value={curso.value}>
                      {curso.label}
                    </option>
                  ))}
                </Form.Control>

                <Form.Group className="mt-3">
                  <Form.Label>Link do YouTube</Form.Label>
                  <Form.Control
                    type="url"
                    id="cp_youtube_link_curso"
                    name="cp_youtube_link_curso"
                    // value={audioData.cp_youtube_link_curso}
                    value={audioData.cp_youtube_link_curso ?? ""}
                    onChange={handleChange}
                    placeholder="Cole o link do YouTube"
                  />
                </Form.Group>
              </div>
            </div>
          </Col>
          <Col md={6}>
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="card-title mb-0">Uploads</h6>
              </div>
              <div className="card-body">
                <Row className="gy-3">
                  <Col md={12}>
                    <div className="upload-wrapper d-flex align-items-center gap-3 flex-wrap">
                      {audioData.cp_audio.length > 0 ? (
                        <div className="uploaded-file-preview position-relative h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-light">
                          <button
                            type="button"
                            className="remove-file position-absolute top-0 end-0 z-1 text-2xxl line-height-1 me-8 mt-8 d-flex"
                            onClick={() => removeFile(0, "audio")}
                          >
                            ×
                          </button>
                          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                            <span style={{ fontSize: "40px" }} className="text-primary text-4xl">
                              🎵
                            </span>
                          </div>
                          <p
                            style={{
                              position: "absolute",
                              bottom: "-18px",
                              left: "50%",
                              transform: "translateX(-50%)",
                              fontSize: "16px",
                              fontWeight: "bold",
                              color: "#333",
                              textAlign: "center",
                              lineHeight: "1.2",
                            }}
                          >
                            <span>({audioData.cp_audio.length})</span>
                            <br />
                            <span>
                              áudio{audioData.cp_audio.length !== 1 ? "s" : ""}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <label
                          className="upload-file-multiple h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-light d-flex align-items-center flex-column justify-content-center gap-1"
                          htmlFor="upload-audio"
                        >
                          <span className="text-secondary-light text-3xl">+</span>
                          <span className="fw-semibold text-secondary-light">
                            Áudio
                          </span>
                          <input
                            id="upload-audio"
                            type="file"
                            hidden
                            name="cp_audio"
                            accept="audio/*"
                            multiple
                            onChange={handleFileChange}
                          />
                        </label>
                      )}
                      {audioData.cp_pdfs.map((pdf, index) => (
                        <div
                          key={index}
                          className="uploaded-file-preview position-relative h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-light"
                        >
                          <button
                            type="button"
                            className="remove-file position-absolute top-0 end-0 z-1 text-2xxl line-height-1 me-8 mt-8 d-flex"
                            onClick={() => removeFile(index, "pdf")}
                          >
                            ×
                          </button>
                          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                            <span style={{ fontSize: "40px" }} className="text-dark text-5xl">
                              📄
                            </span>
                          </div>
                        </div>
                      ))}
                      {audioData.cp_pdfs.length < 3 && (
                        <label
                          className="upload-file-multiple h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-light d-flex align-items-center flex-column justify-content-center gap-1"
                          htmlFor="upload-pdfs"
                        >
                          <span className="text-secondary-light text-3xl">+</span>
                          <span className="fw-semibold text-secondary-light">
                            PDFs
                          </span>
                          <input
                            id="upload-pdfs"
                            type="file"
                            hidden
                            name="cp_pdfs"
                            accept="application/pdf"
                            multiple
                            onChange={handleFileChange}
                          />
                        </label>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>
            </div>
          </Col>
        </Row>
        <div className="mt-4 text-center">
          <Button type="button" variant="outline-secondary" className="me-2" onClick={goBack}>
            Voltar
          </Button>
          <Button type="button" variant="primary" onClick={() => setShowConfirmModal(true)}>
            {audioID ? "Salvar Alterações" : "Cadastrar Áudio"}
          </Button>
        </div>
      </form>
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirmar Cadastro</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Tem certeza de que deseja {audioID ? "salvar as alterações" : "cadastrar este áudio"}?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={(e) => handleConfirmSubmit(e)}>
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>
      <ToastContainer containerId={AUDIO_TOAST_CONTAINER_ID} />
    </div>
  );
};

export default CadastroAudio;
